import React from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { LaneStatus, UnitStatus } from '../../../workflow/workflowConfig';

// A single small glyph summarising a lane's status, reused on tab labels, in the table's Stage
// column, and inside the lane tabs so the same state always looks the same.
export function laneStatusGlyph(status: LaneStatus, size = 16): React.ReactNode {
  const sx = { fontSize: size };
  switch (status) {
    case 'passed':
      return <Tooltip title="Passed"><CheckCircleIcon color="success" sx={sx} /></Tooltip>;
    case 'failed':
      return <Tooltip title="Failed"><CancelIcon color="error" sx={sx} /></Tooltip>;
    case 'verify':
      return <Tooltip title="Needs verification"><FactCheckOutlinedIcon color="info" sx={sx} /></Tooltip>;
    case 'running':
      return <Tooltip title="Running"><CircularProgress size={size - 2} /></Tooltip>;
    case 'needed':
      return <Tooltip title="Needed"><WarningAmberIcon color="warning" sx={sx} /></Tooltip>;
    case 'retest-decision':
      return <Tooltip title="Retest decision needed"><HelpOutlineIcon color="warning" sx={sx} /></Tooltip>;
    case 'na':
    default:
      return <Tooltip title="Not applicable"><RemoveCircleOutlineIcon sx={{ ...sx, color: 'text.disabled' }} /></Tooltip>;
  }
}

export function unitStatusGlyph(status: UnitStatus, size = 16): React.ReactNode {
  // Unit lane has a narrower vocabulary; reuse the lane glyphs for the shared states.
  const mapped: LaneStatus = status === 'na' ? 'na' : (status as LaneStatus);
  return laneStatusGlyph(mapped, size);
}

export function laneStatusLabel(status: LaneStatus): string {
  switch (status) {
    case 'passed': return 'Passed';
    case 'failed': return 'Failed';
    case 'verify': return 'Needs verification';
    case 'running': return 'Running';
    case 'needed': return 'Needed — not yet run';
    case 'retest-decision': return 'Retest decision needed';
    case 'na':
    default: return 'Not applicable';
  }
}
